/**
 * Automation tab copy, types, and defaults.
 * Ref: AUTOMATION_TAB_UI_SPEC.md v3.0 | UI-02 | UI-03
 */

/** channel_settings fields for master automation controls (UI-02) */
export interface ChannelAutomationSettings {
  auto_draft_enabled: boolean;
  auto_draft_interval_minutes: number;
}

/** Opt-in: absent API channel_settings must not show Auto-Draft as ON before first save */
export const DEFAULT_CHANNEL_AUTOMATION_SETTINGS: ChannelAutomationSettings = {
  auto_draft_enabled: false,
  auto_draft_interval_minutes: 5,
};

export const CHANNEL_AUTOMATION_INTERVAL_OPTIONS = [2, 5, 10, 15, 30] as const;

export type ChannelAutomationIntervalMinutes = (typeof CHANNEL_AUTOMATION_INTERVAL_OPTIONS)[number];

/** UI-02 master toggle tooltip — verbatim */
export const MASTER_AUTO_DRAFT_TOOLTIP = `Master switch for this channel's automation.

ON  — Prism7 checks for new emails at your chosen interval
      and generates drafts based on the label settings below.

OFF — All automation stops for this channel. Nothing is
      generated or sent. Your label settings are saved and
      will resume when you turn this back on.

Use this as an emergency stop — for example, during a
planned maintenance window or if something unexpected happens.`;

/** UI-02 interval dropdown tooltip — verbatim */
export const CHECK_INTERVAL_TOOLTIP = `How often Prism7 checks for new emails that need a draft.

  2 min   Near real-time. Good for urgent support channels.
  5 min   Recommended for most channels.
  10 min  Lower activity channels.
  15 min  Batched cadence.
  30 min  Very low-volume channels.

Tip: New emails always wait at least 2 minutes before a
draft is generated — this gives you time to start typing
your own reply first.`;

/** UI-02 master OFF amber banner — verbatim */
export const MASTER_OFF_BANNER_TEXT =
  'Auto-Draft is OFF. No drafts will be generated for any label until you turn this back on. Your label settings are saved.';

/** UI-03 section info banner — verbatim copy, single paragraph for full-width wrap */
export const LABEL_SETTINGS_INFO_BANNER =
  'Each label runs independently. Configure Auto-Draft and Auto-Reply separately for each label. Labels not turned on here are skipped entirely — no drafts generated.';

/** UI-03 Open sensitivity warning — verbatim copy, single paragraph for full-width wrap */
export const OPEN_SENSITIVITY_WARNING =
  'Open sensitivity means Prism7 may reply even when less certain. Review a sample of auto-sent replies before using this on customer-facing labels.';

/** UI-03 column header tooltips — verbatim */
export const COLUMN_TOOLTIP_AUTO_DRAFT = `Should Prism7 generate a draft reply for emails with this label?

ON  — Prism7 generates a draft. It appears in your inbox
      for review. Nothing is sent unless Auto-Reply is also ON.

OFF — Emails with this label are skipped. No draft created.`;

export const COLUMN_TOOLTIP_AUTO_REPLY = `Should Prism7 automatically send the draft — without you
reviewing it — when it meets the confidence threshold?

This requires Auto-Draft to be ON for the same label.

ON  — Prism7 sends the reply automatically if the draft
      quality meets your Sensitivity setting.

OFF — Draft is generated but not sent. You review it first.`;

export const COLUMN_TOOLTIP_SENSITIVITY = `How confident must Prism7 be before auto-replying?

Prism7 scores each draft using two signals:
  1. Label confidence  — how clearly the email matches this label
  2. Draft quality     — how completely the draft answers the email

These combine into a single score. Sensitivity sets the minimum
score required to send automatically.

  Careful   Highest bar. Fewer auto-replies, very accurate.
  Balanced  Good balance. Handles routine emails well.
  Open      Lower bar. More replies, some may miss the mark.

Only shown when Auto-Reply is ON for this label.`;

/** UI-04 automation guidance banner — state-aware copy */
export type AutomationBannerState = 'no_labels' | 'master_off' | 'active' | 'setup_pending';

export const AUTOMATION_BANNER = {
  dismissButton: 'Dismiss',
  configureLabelsButton: 'Configure labels',
  reviewLabelsButton: 'Review labels',
  noLabels: {
    title: 'Get started with Automation',
    bodyLines: [
      'Enable Auto-Draft per label to have Prism7 generate reply drafts automatically.',
      'To send replies without review, also turn on Auto-Reply for that label.',
    ],
  },
  masterOff: {
    title: 'Auto-Draft is off',
    body: 'Your label settings are saved. Turn on Auto-Draft at the channel level to start generating drafts.',
  },
  active: {
    title: 'Automation is active',
    body: 'Prism7 will generate drafts for enabled labels. Replies are saved for your review unless Auto-Reply is also on.',
  },
} as const;

export interface AutomationBannerCopy {
  title: string;
  bodyLines: string[];
  primaryButton: string;
  dismissButton: string;
}

/** Resolve banner state from saved label config and current UI toggles */
export function getAutomationBannerState(
  labelsFromApiEmpty: boolean,
  masterEnabled: boolean,
  labelRows: LabelAutomationSetting[]
): AutomationBannerState {
  if (labelsFromApiEmpty) return 'no_labels';
  if (!masterEnabled) return 'master_off';
  if (labelRows.some((row) => row.auto_draft_enabled)) return 'active';
  return 'setup_pending';
}

/** Map banner state to headline, body, and CTA label */
export function getAutomationBannerCopy(state: AutomationBannerState): AutomationBannerCopy {
  const dismissButton = AUTOMATION_BANNER.dismissButton;

  switch (state) {
    case 'no_labels':
      return {
        title: AUTOMATION_BANNER.noLabels.title,
        bodyLines: [...AUTOMATION_BANNER.noLabels.bodyLines],
        primaryButton: AUTOMATION_BANNER.configureLabelsButton,
        dismissButton,
      };
    case 'master_off':
      return {
        title: AUTOMATION_BANNER.masterOff.title,
        bodyLines: [AUTOMATION_BANNER.masterOff.body],
        primaryButton: AUTOMATION_BANNER.reviewLabelsButton,
        dismissButton,
      };
    case 'active':
      return {
        title: AUTOMATION_BANNER.active.title,
        bodyLines: [AUTOMATION_BANNER.active.body],
        primaryButton: AUTOMATION_BANNER.reviewLabelsButton,
        dismissButton,
      };
    case 'setup_pending':
      return {
        title: AUTOMATION_BANNER.noLabels.title,
        bodyLines: [...AUTOMATION_BANNER.noLabels.bodyLines],
        primaryButton: AUTOMATION_BANNER.reviewLabelsButton,
        dismissButton,
      };
  }
}

/** @deprecated Use getAutomationBannerCopy */
export const FIRST_TIME_BANNER = {
  title: AUTOMATION_BANNER.noLabels.title,
  body: AUTOMATION_BANNER.noLabels.bodyLines.join('\n'),
  configureButton: AUTOMATION_BANNER.configureLabelsButton,
  dismissButton: AUTOMATION_BANNER.dismissButton,
} as const;

/** UI-04 no subtags empty state — verbatim */
export const NO_SUBTAGS_EMPTY_STATE = {
  heading: 'No labels configured for this channel yet.',
  instructionsPrefix: 'Add subtags in',
  instructionsLink: 'Channel Config → Edit → Sub tags',
  instructionsSuffix: "and they'll appear here to configure.",
  footnote:
    'Auto-Draft requires labels to work — emails with no matching label are skipped automatically.',
} as const;

export interface NoSubtagsEmptyStateCopy {
  heading: string;
  instructionsPrefix: string;
  instructionsLink: string;
  instructionsSuffix: string;
  footnote: string;
}

/** Empty-state copy for Configure messaging wizard (step 0 = Messaging Provider) */
export const NO_SUBTAGS_EMPTY_STATE_MESSAGING_SETUP = {
  heading: 'No labels configured for this channel yet.',
  instructionsPrefix: 'Add keywords in the',
  instructionsLink: 'Messaging Provider',
  instructionsSuffix: 'step to configure automation labels.',
  footnote:
    'Auto-Draft requires labels to work — messages with no matching label are skipped automatically.',
} as const;

/** Empty-state copy for Configure email wizard (step 0 = Email Connection keywords) */
export const NO_SUBTAGS_EMPTY_STATE_EMAIL_SETUP = {
  heading: 'No labels configured for this channel yet.',
  instructionsPrefix: 'Add keywords in the',
  instructionsLink: 'Email Connection',
  instructionsSuffix: 'step to configure automation labels.',
  footnote:
    'Auto-Draft requires labels to work — emails with no matching label are skipped automatically.',
} as const;

/** @deprecated Use NO_SUBTAGS_EMPTY_STATE */
export const AUTOMATION_COPY = {
  noSubtagsEmpty: NO_SUBTAGS_EMPTY_STATE.heading,
  noSubtagsLink: NO_SUBTAGS_EMPTY_STATE.instructionsLink,
  noSubtagsSuffix: NO_SUBTAGS_EMPTY_STATE.instructionsSuffix,
} as const;

/** UI-03: Careful / Balanced / Open — no channel default */
export const SENSITIVITY_OPTIONS = [
  { value: 'careful', label: 'Careful' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'open', label: 'Open' },
] as const;

export type SensitivityValue = (typeof SENSITIVITY_OPTIONS)[number]['value'];

const SENSITIVITY_LABEL: Record<SensitivityValue, string> = {
  careful: 'Careful',
  balanced: 'Balanced',
  open: 'Open',
};

/** Stored under channel_settings.labels.{label_name} */
export interface LabelChannelSettings {
  auto_draft_enabled: boolean;
  auto_reply_enabled?: boolean;
  auto_reply_sensitivity?: SensitivityValue;
}

/** Row model for the label settings table */
export interface LabelAutomationSetting {
  name: string;
  auto_draft_enabled: boolean;
  auto_reply_enabled?: boolean;
  auto_reply_sensitivity?: SensitivityValue;
}

export function getSensitivityLabel(value: SensitivityValue | undefined): string {
  if (!value) return 'Balanced';
  return SENSITIVITY_LABEL[value] ?? 'Balanced';
}

/** UI-03 per-row Auto-Draft tooltip — state-aware, verbatim */
export function getAutoDraftRowTooltip(labelName: string, enabled: boolean): string {
  if (enabled) {
    return `Auto-Draft is ON for #${labelName}.
Prism7 will generate a draft reply for every new email
classified under this label.`;
  }
  return `Auto-Draft is OFF for #${labelName}.
Emails with this label are skipped — no draft is created.
Turn ON to include this label in automatic drafting.`;
}

/** UI-03 per-row Auto-Reply tooltip — state-aware, verbatim */
export function getAutoReplyRowTooltip(
  labelName: string,
  draftOn: boolean,
  replyOn: boolean,
  sensitivity?: SensitivityValue
): string {
  if (!draftOn) {
    return `Auto-Draft is OFF for this label.
Enable Auto-Draft for #${labelName} first before
turning on Auto-Reply.`;
  }
  if (replyOn) {
    const tier = getSensitivityLabel(sensitivity);
    return `Auto-Reply is ON for #${labelName}.
Prism7 will send replies automatically when the draft
meets the ${tier} confidence threshold.
The draft is still saved in your inbox for reference.`;
  }
  return `Auto-Reply is OFF for #${labelName}.
Prism7 will generate a draft but wait for you to send it.
Turn ON to let Prism7 send replies automatically for this label.`;
}

/** @deprecated Use DEFAULT_CHANNEL_AUTOMATION_SETTINGS */
export const MOCK_CHANNEL_SETTINGS = DEFAULT_CHANNEL_AUTOMATION_SETTINGS;

/** @deprecated Use CHANNEL_AUTOMATION_INTERVAL_OPTIONS */
export const INTERVAL_OPTIONS = CHANNEL_AUTOMATION_INTERVAL_OPTIONS;
